/**
 * Card Token Mapper
 *
 * Converts PCI Tokeniser CardTokenResponse to UI SavedCard format
 */

import type {
  CardTokenResponse,
  SavedCard,
  CardType,
  CardExpiryStatus,
} from '../types/voice-dtmf.types';

/**
 * Map card scheme to CardType
 *
 * @param cardScheme - Card scheme from PCI Tokeniser (VISA, MASTERCARD, AMEX, etc.)
 * @returns CardType enum value
 */
function mapCardSchemeToType(cardScheme: string): CardType {
  const normalized = cardScheme.toUpperCase();

  switch (normalized) {
    case 'VISA':
      return 'VISA';
    case 'MASTERCARD':
    case 'MASTER':
      return 'MASTERCARD';
    case 'AMEX':
    case 'AMERICAN_EXPRESS':
      return 'AMEX';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Calculate card expiry status
 *
 * Aligned with Payment Checkout UI logic:
 * - EXPIRED: Card has expired (past month)
 * - EXPIRING: Card expires within 2 months (warning)
 * - ACTIVE: Card valid for 2+ months
 *
 * @param expiryMonth - 2-digit month (01-12)
 * @param expiryYear - 4-digit year (2026)
 * @returns Card expiry status
 */
export function calculateExpiryStatus(
  expiryMonth: string,
  expiryYear: string
): CardExpiryStatus {
  const month = parseInt(expiryMonth, 10);
  const year = parseInt(expiryYear, 10);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 0-indexed

  // Expired: past expiry date
  if (year < currentYear) {
    return 'EXPIRED';
  }

  if (year === currentYear && month < currentMonth) {
    return 'EXPIRED';
  }

  // Expiring soon: within 2 months (current month + 1 month ahead)
  const monthsUntilExpiry = (year - currentYear) * 12 + (month - currentMonth);

  if (monthsUntilExpiry <= 1) {
    return 'EXPIRING';
  }

  // Active: 2+ months remaining
  return 'ACTIVE';
}

/**
 * Format expiry date to MM/YY
 *
 * @param expiryMonth - 2-digit month (01-12)
 * @param expiryYear - 4-digit year (2026)
 * @returns Formatted expiry date (e.g., "03/26")
 */
function formatExpiryDate(expiryMonth: string, expiryYear: string): string {
  const month = expiryMonth.padStart(2, '0');
  const year = expiryYear.slice(-2); // Last 2 digits
  return `${month}/${year}`;
}

/**
 * Format masked card number with Sycurio standard spacing
 *
 * @param last4Digits - Last 4 digits of card
 * @param cardScheme - Card scheme (for determining format)
 * @returns Masked card number (e.g., "4111 11** **** 4456")
 */
function formatMaskedCardNumber(
  last4Digits: string,
  cardScheme: string
): string {
  const cardType = mapCardSchemeToType(cardScheme);

  // American Express uses 4-6-5 format
  if (cardType === 'AMEX') {
    // Example: "3782 82** **** 1005"
    return `37** **** **** ${last4Digits}`;
  }

  // Default: 4-4-4-4 format
  // Example: "4111 11** **** 4456"
  return `**** **** **** ${last4Digits}`;
}

/**
 * Generate card display name
 *
 * @param cardScheme - Card scheme
 * @param last4Digits - Last 4 digits
 * @param friendlyName - Optional friendly name
 * @returns Display name (e.g., "Visa 4456" or "Personal Visa")
 */
function generateCardName(
  cardScheme: string,
  last4Digits: string,
  friendlyName?: string
): string {
  if (friendlyName) {
    return friendlyName;
  }

  const cardType = mapCardSchemeToType(cardScheme);
  const typeName = cardType === 'UNKNOWN' ? cardScheme : cardType;

  // Capitalize first letter only (Visa, Mastercard, Amex)
  const displayType =
    typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase();

  return `${displayType} ${last4Digits}`;
}

/**
 * Convert CardTokenResponse to SavedCard
 *
 * Transforms PCI Tokeniser response format to UI format
 *
 * @param token - Card token from PCI Tokeniser
 * @returns SavedCard for UI display
 */
export function convertCardTokenToSavedCard(
  token: CardTokenResponse
): SavedCard {
  const expiryStatus = calculateExpiryStatus(
    token.expiryMonth,
    token.expiryYear
  );

  return {
    cardId: token.id,
    cardName: generateCardName(
      token.cardScheme,
      token.last4Digits,
      token.friendlyName
    ),
    maskedCardNumber: formatMaskedCardNumber(
      token.last4Digits,
      token.cardScheme
    ),
    expiryDate: formatExpiryDate(token.expiryMonth, token.expiryYear),
    expiryMonth: token.expiryMonth,
    expiryYear: token.expiryYear,
    cardType: mapCardSchemeToType(token.cardScheme),
    expiryStatus: expiryStatus,
    isExpired: expiryStatus === 'EXPIRED', // Deprecated - kept for backwards compatibility
  };
}

/**
 * Convert array of CardTokenResponse to SavedCard array
 *
 * @param tokens - Card tokens from PCI Tokeniser
 * @returns Array of SavedCard for UI display
 */
export function convertCardTokensToSavedCards(
  tokens: CardTokenResponse[]
): SavedCard[] {
  return tokens
    .filter((token) => token.status === 'ACTIVE') // Extra safety: filter ACTIVE only
    .map(convertCardTokenToSavedCard);
}
